import { gameKeyMatchesUrl } from '@/services/analyticsGameKey';
import { normalizeSport } from '@/stores/marketFrameNormalization';
import { useMarketStore } from '@/stores/marketStore';
import { MULTI_OUTCOME_SPORTS, SPORT_TABS } from '@/views/ValueDashboard/types';
import type { GameEventRow, GameRow, MultiOutcomeEvent, SeriesOutcome, Sport } from '@/views/ValueDashboard/types';
import { multiOutcomeRoles } from '@/views/ValueDashboard/multiOutcome';
import { synthesizeFromSpread, synthesizeFromTotal } from '@/views/SpreadsTotals/synthesize';

export interface GameUrlParts {
  sport?: string;
  date?: string;
  teamA?: string;
  teamB?: string;
  gameNo?: string;
}

const ALL_SPORTS = SPORT_TABS.map(tab => tab.id as Sport);
const ALL_DATES_AND_STATUSES = { dateFilter: 'all', statusFilter: 'all' } as const;

/** An explicit route already identifies the sport. Do not build unrelated
 * inventories to rediscover it on every feed publication. Legacy sport-less
 * and unknown routes retain their old search order and exact key matching.
 * Normalize with the same vocabulary as the matcher (including CFB/ncaaf).
 * No result cache: quotes, new/removed lines, and identity corrections remain
 * visible on the existing publication clock.
 *
 * Tennis tours scan ONLY the route's tour (#1284). Both tours share hydra's
 * tour-less `tennis|date|1|LO|HI` key, so the key matches either tour's
 * route; the tab a row is filed under is the only tour evidence. Scanning
 * every sport (the pre-#1284 behaviour, for ITF rows that move tours) would
 * let `/game/atp/…` open a WTA match with the same date and surnames. A link
 * minted before such a move resolves to not-found rather than to the other
 * tour's match. */
function lookupSports(url: GameUrlParts): readonly Sport[] {
  const sport = normalizeSport(url.sport?.trim().toLowerCase() ?? '');
  return sport && ALL_SPORTS.includes(sport) ? [sport] : ALL_SPORTS;
}

export function findGameRow(url: GameUrlParts): GameRow | null {
  const store = useMarketStore.getState();
  for (const sport of lookupSports(url)) {
    const hit = store.getGames(sport, 'moneyline', ALL_DATES_AND_STATUSES)
      .find(game => gameKeyMatchesUrl(game.key, url));
    if (hit) return hit;
  }
  return null;
}

interface MultiOutcomeGame {
  event: MultiOutcomeEvent;
  away?: SeriesOutcome;
  home?: SeriesOutcome;
  draw?: SeriesOutcome;
}

export function findMultiOutcomeGame(url: GameUrlParts): MultiOutcomeGame | null {
  const store = useMarketStore.getState();
  for (const sport of lookupSports(url)) {
    if (!MULTI_OUTCOME_SPORTS.has(sport)) continue;
    const event = store.getMultiOutcomeEvents(sport).find(game => gameKeyMatchesUrl(game.key, url));
    if (event) return { event, ...multiOutcomeRoles(event) };
  }
  return null;
}

export function findGameEvent(url: GameUrlParts): GameEventRow | null {
  const store = useMarketStore.getState();
  for (const sport of lookupSports(url)) {
    const hit = store.getGameEvents(sport, ALL_DATES_AND_STATUSES)
      .find(game => gameKeyMatchesUrl(game.key, url));
    if (hit) return hit;
  }
  return null;
}

/** #1184: does this route resolve a game Game Detail can render sections
 * for, from rows ALREADY in the store? The same finders the page uses (binary
 * row, a multi-outcome away/home pair, or the spread/total event), so "has a
 * game" is decided in one place. With none of them the page is a header plus
 * an empty consensus card. Tennis included: since #1284 an ATP/WTA route
 * scans only its own tour, so a match resolves on its tour's page alone.
 *
 * Point-in-time and store-only: false means "not in the rows loaded now",
 * never "the market retired" — callers must not word it that way, and must
 * re-ask when the store publishes. Issues no request and declares no feed. */
export function gameDetailReceives(url: GameUrlParts): boolean {
  if (findGameRow(url)) return true;
  const multi = findMultiOutcomeGame(url);
  if (multi?.away && multi.home) return true;
  return findGameEvent(url) != null;
}

/** Reuse the event the page resolved in this render; rebuilding its complete
 * spread/total inventory a second time cannot provide a fresher snapshot.
 * Preserve exact ticker selection and the caller's existing missing-line
 * behavior. This helper neither caches a result nor changes execution gates. */
export function findLiveSynthGameRow(synthKey: string, event: GameEventRow | null): GameRow | null {
  const parts = synthKey.split('|');
  if (parts.length < 4 || !event) return null;
  const ticker = parts[parts.length - 1];
  const kind = parts[parts.length - 2];
  if (kind === 'spread') {
    const line = event.spreads.find(s => s.kalshi?.marketTicker === ticker || s.polymarket?.marketTicker === ticker);
    return line ? synthesizeFromSpread(event, line) : null;
  }
  if (kind === 'total') {
    const line = event.totals.find(t => t.kalshi?.marketTicker === ticker || t.polymarket?.marketTicker === ticker);
    return line ? synthesizeFromTotal(event, line) : null;
  }
  return null;
}
